import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workersAPI, reviewsAPI, portfolioAPI } from '../api/api';
import { Progress } from '../components/ui/progress';
import { ReviewsList, ReviewForm, StarRating } from '../components/Reviews';
import AvailabilityBadge from '../components/AvailabilityBadge';
import PublicAvailabilityCalendar from '../components/PublicAvailabilityCalendar';
import VerifiedBadges from '../components/VerifiedBadges';
import { PortfolioGallery } from '../components/Portfolio';
import { 
  Star, 
  MapPin, 
  Briefcase, 
  CalendarBlank, 
  Phone, 
  EnvelopeSimple,
  Trophy,
  CheckCircle,
  Clock,
  CurrencyDollar,
  ArrowLeft,
  ChatCircle,
  PencilSimple,
  Image,
  SpinnerGap,
  ShieldCheck,
  User
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const WorkerProfile = () => {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [canReview, setCanReview] = useState({ can_review: false });
  const [activeTab, setActiveTab] = useState('overview');
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);

  const currentUserId = localStorage.getItem('userId');
  const isOwnProfile = currentUserId === workerId;

  const fetchWorkerProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await workersAPI.getById(workerId);
      setWorker(response.data.worker);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load worker profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  const fetchPortfolio = useCallback(async () => {
    try {
      setLoadingPortfolio(true);
      const response = await portfolioAPI.getWorkerPortfolio(workerId);
      setPortfolioItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoadingPortfolio(false);
    }
  }, [workerId]);

  useEffect(() => {
    fetchWorkerProfile();
    fetchPortfolio();
    checkCanReview();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [fetchWorkerProfile, fetchPortfolio]);

  const checkCanReview = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await reviewsAPI.canReview(workerId);
      setCanReview(response.data);
    } catch (error) {
      console.error('Error checking review status:', error);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    setCanReview({ can_review: false, reason: 'Already reviewed' });
    fetchWorkerProfile();
  };

  const handleContact = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please log in to message this worker',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }
    navigate(`/messages?partner=${workerId}`);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'portfolio', label: 'Portfolio', icon: Image },
    { id: 'reviews', label: 'Reviews', icon: Star },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <SpinnerGap size={64} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <div className="text-center">
          <User size={64} weight="light" className="text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Worker Not Found</h2>
          <button 
            onClick={() => navigate('/find-workers')}
            className="h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black"
          >
            Find Workers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="worker-profile-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/90 hover:text-white font-bold mb-4"
          >
            <ArrowLeft size={24} weight="bold" />
            Back
          </button>
          
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-32 md:h-32 bg-white/20 rounded-xl flex items-center justify-center border-4 border-white/30 overflow-hidden flex-shrink-0">
              {worker.avatar ? (
                <img src={worker.avatar} alt={worker.name} className="w-full h-full object-cover" />
              ) : (
                <User size={48} weight="bold" className="text-white" />
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-black">{worker.name}</h1>
                {worker.verified && (
                  <ShieldCheck size={28} weight="fill" className="text-yellow-300" />
                )}
              </div>
              <p className="text-blue-100 text-lg mb-3">{worker.trade || 'Construction Worker'}</p>
              
              {/* Granular trust badges */}
              <div className="mb-3">
                <VerifiedBadges
                  licensed={!!worker.verified}
                  insured={!!worker.verified}
                  idVerified={!!(worker.id_verified || worker.verified)}
                  testIdPrefix="worker-trust"
                />
              </div>
              
              {/* Rating */}
              <div className="flex items-center gap-2 mb-3">
                <Star size={28} weight="fill" className="text-yellow-300" />
                <span className="text-2xl font-black">{worker.rating?.toFixed(1) || '0.0'}</span>
                <span className="text-blue-100">({worker.review_count || 0} reviews)</span>
              </div>
              
              {/* Location */}
              <div className="flex items-center gap-2 text-blue-100">
                <MapPin size={20} weight="bold" />
                <span>{worker.location || 'Location not specified'}</span>
              </div>

              {/* Availability Badge */}
              <div className="mt-3">
                <AvailabilityBadge workerId={workerId} size="md" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button 
            onClick={handleContact}
            className="flex items-center gap-2 h-14 px-6 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="contact-btn"
          >
            <ChatCircle size={24} weight="bold" />
            Contact
          </button>
          {isOwnProfile && (
            <button 
              onClick={() => navigate('/worker-control-panel?tab=profile')}
              className="flex items-center gap-2 h-14 px-6 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              data-testid="edit-profile-btn"
            >
              <PencilSimple size={24} weight="bold" />
              Edit Profile
            </button>
          )}
          {canReview.can_review && (
            <button 
              onClick={() => setShowReviewForm(true)}
              className="flex items-center gap-2 h-14 px-6 bg-white text-gray-900 font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              data-testid="write-review-btn"
            >
              <Star size={24} weight="bold" />
              Write Review
            </button>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-2 border-black rounded-xl p-2 mb-6 shadow-[4px_4px_0px_#000]">
          <div className="flex gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg font-bold transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#2B4C6F] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={24} weight="bold" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
                <Briefcase size={28} weight="bold" className="text-[#2B4C6F] mb-2" />
                <p className="text-sm font-bold text-gray-500">Jobs Done</p>
                <p className="text-2xl font-black text-gray-900">{worker.completed_jobs || 0}</p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
                <Clock size={28} weight="bold" className="text-green-600 mb-2" />
                <p className="text-sm font-bold text-gray-500">Experience</p>
                <p className="text-2xl font-black text-gray-900">{worker.experience || '3'}+ yrs</p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
                <CheckCircle size={28} weight="fill" className="text-green-600 mb-2" />
                <p className="text-sm font-bold text-gray-500">Success Rate</p>
                <p className="text-2xl font-black text-gray-900">{worker.success_rate || 98}%</p>
              </div>
              <div className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000]">
                <CurrencyDollar size={28} weight="bold" className="text-purple-600 mb-2" />
                <p className="text-sm font-bold text-gray-500">Hourly Rate</p>
                <p className="text-2xl font-black text-gray-900">
                  {(() => {
                    const raw = String(worker.hourly_rate ?? '45');
                    // Extract digits (handles legacy formats like "$75/hour" or "75")
                    const match = raw.match(/\d+(?:\.\d+)?/);
                    const num = match ? match[0] : raw.replace(/[$/hr\s]/gi, '') || '45';
                    return `$${num}/hr`;
                  })()}
                </p>
              </div>
            </div>

            {/* About */}
            {worker.bio && (
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-4">About</h2>
                <p className="text-gray-700 text-lg leading-relaxed">{worker.bio}</p>
              </div>
            )}

            {/* Skills */}
            {worker.skills && worker.skills.length > 0 && (
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-4">Skills</h2>
                <div className="flex flex-wrap gap-3">
                  {worker.skills.map((skill, idx) => (
                    <span 
                      key={idx}
                      className="flex items-center gap-2 px-4 py-2 bg-[#2B4C6F] text-white font-bold rounded-full"
                    >
                      <CheckCircle size={20} weight="fill" />
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Availability Calendar */}
            <PublicAvailabilityCalendar userId={workerId} userName={worker.name} />

            {/* Contact Info */}
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <h2 className="text-xl font-black text-gray-900 mb-4">Contact Information</h2>
              <div className="space-y-3">
                {worker.email && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <EnvelopeSimple size={24} weight="bold" className="text-gray-400" />
                    <span className="font-medium">{worker.email}</span>
                  </div>
                )}
                {worker.phone && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone size={24} weight="bold" className="text-gray-400" />
                    <span className="font-medium">{worker.phone}</span>
                  </div>
                )}
                {worker.location && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <MapPin size={24} weight="bold" className="text-gray-400" />
                    <span className="font-medium">{worker.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'portfolio' && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
            <h2 className="text-xl font-black text-gray-900 mb-4">Portfolio</h2>
            {loadingPortfolio ? (
              <div className="flex justify-center py-8">
                <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
              </div>
            ) : portfolioItems.length > 0 ? (
              <PortfolioGallery items={portfolioItems} />
            ) : (
              <div className="text-center py-8">
                <Image size={64} weight="light" className="text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No portfolio items yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {showReviewForm && (
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h2 className="text-xl font-black text-gray-900 mb-4">Write a Review</h2>
                <ReviewForm
                  workerId={workerId}
                  onSuccess={handleReviewSuccess}
                  onCancel={() => setShowReviewForm(false)}
                />
              </div>
            )}
            
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <h2 className="text-xl font-black text-gray-900 mb-4">
                Reviews ({worker.review_count || 0})
              </h2>
              <ReviewsList workerId={workerId} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerProfile;
